import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { BasicInputComponent } from '../basic-input/basic-input.component'

@Component({
  selector: 'slug',
  templateUrl: './slug.component.html',
  styleUrls: [],
  standalone: true,
  imports: [BasicInputComponent]
})
export class SlugComponent implements OnInit {
  /**
   * The form control for the slug input.
   */
  @Input() control!: FormControl
  /**
   * The label for the slug input field.
   */
  @Input() label: string = 'Organization acronym (URL, Slug)'
  /**
   * Id for the slug input field.
   */
  @Input() id: string = 'slug-input'
  /**
   * The svg icon for the slug input field.
   */
  @Input() prefixSlug: string = `https://events.micetribe.com/`
  icon: string = `<svg class="tw-w-6 tw-h-6 tw-text-gray-800 dark:tw-text-white" aria-hidden="true" fill="none" viewBox="0 0 19 19">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.013 7.962a3.519 3.519 0 0 0-4.975 0l-3.554 3.554a3.518 3.518 0 0 0 4.975 4.975l.461-.46m-.461-4.515a3.518 3.518 0 0 0 4.975 0l3.553-3.554a3.518 3.518 0 0 0-4.974-4.975L10.3 3.7"/>
</svg>`
  /**
   * Whether the slug input field is required.
   */
  @Input() required: boolean = false

  @Output() onBlurValueChange: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>()

  public basicFieldType = BasicFieldType.SLUG
  private _destroyRef = inject(DestroyRef)
  private readonly _maxLength = 16
  private readonly _minLength = 3

  ngOnInit() {
    // add the validators and async validators on the form control.
    this.control.addValidators([Validators.maxLength(this._maxLength), Validators.minLength(this._minLength)])
    this.control.updateValueAndValidity({ emitEvent: false })

    this.control.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        const newValue = this.enforceInputRules(value)
        this.control.setValue(newValue, { emitEvent: false })
      })
  }


  /**
   * Enforces input rules on the slug.
   * - It cannot contain spaces.
   * - It cannot contain special characters other than '-' and '_'.
   * - It cannot be longer than 16 characters.
   * @param {string | null} inputValue - The current input value.
   * @returns {string} - The new input value.
   */
  enforceInputRules(inputValue: string | null): string {
    if (!inputValue) return ''

    let newValue = inputValue

    // Ensure the input does not contain spaces
    newValue = newValue.replace(/\s/g, '')

    // Ensure the input does not contain special characters other than '-' and '_'
    newValue = newValue.replace(/[^a-zA-Z0-9-_]/g, '')

    // Ensure the input is no longer than max length
    if (newValue.length > this._maxLength) {
      newValue = newValue.slice(0, this._maxLength)
    }

    newValue = this._normalizeSlug(newValue)

    return newValue
  }

  /**
   * Forward blur event to parent
   * @param {FocusEvent} event
   */
  public onBlurEvent(event: FocusEvent): void {
    this.onBlurValueChange.emit(event)
  }

  /**
   * Normalize the slug by removing the http://, https://, and www. prefixes
   * @param slug String to normalize
   * @returns Normalized slug
   */
  private _normalizeSlug(slug: string): string {
    const regexToRemove = /^(?:https?:\/\/|www\.)/i
    const normalizedSlug = slug.replace(regexToRemove, '')
    return normalizedSlug
  }
}
